import {FC, useEffect, useState} from 'react'
import Swal from 'sweetalert2'
import {ID} from '../../../_metronic/helpers'
import {ProductMachine} from '../core/_models'
import {getProductMachines, syncProductMachines} from '../core/_request'

type Props = {
  productId: ID
}

export const ProductMachines: FC<Props> = ({productId}) => {
  const [machines, setMachines] = useState<ProductMachine[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      setMachines(await getProductMachines(productId))
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error?.response?.data?.message ?? 'Erro ao carregar as máquinas do produto',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  const update = (id: number, changes: Partial<ProductMachine>) =>
    setMachines((current) =>
      current.map((machine) => (machine.id === id ? {...machine, ...changes} : machine))
    )

  const linked = machines.filter((machine) => machine.linked)
  const invalid = linked.filter((machine) => !(machine.sessionTime >= 1))

  const save = async () => {
    const result = await Swal.fire({
      title: 'Deseja salvar as máquinas do produto?',
      text: `${linked.length} máquina(s) vinculada(s).`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary',
      },
    })
    if (!result.isConfirmed) return

    setSaving(true)
    try {
      await syncProductMachines(productId, {
        machines: linked.map((machine) => ({id: machine.id, sessionTime: machine.sessionTime})),
      })
      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Salvo com sucesso',
        showConfirmButton: false,
        timer: 1500,
      })
      await load()
    } catch (error: any) {
      let text = error?.response?.data?.message
      if (error?.response?.status === 422) {
        text = Object.values(error.response.data.errors ?? {})
          .map((messages: any) => messages[0])
          .join('<br/>')
      }
      await Swal.fire({icon: 'error', title: 'Oops...', html: text})
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column'>
          <h3 className='fw-bold mb-1'>Máquinas</h3>
          <span className='text-muted fs-7'>
            Máquinas que executam o produto e o tempo de sessão em cada uma. No agendamento com
            máquina, a duração vem deste tempo.
          </span>
        </div>
      </div>

      <div className='card-body py-4'>
        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <span className='spinner-border text-danger' />
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-3'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className='w-25px'></th>
                  <th className='min-w-200px'>Máquina</th>
                  <th className='min-w-150px'>Tempo de sessão (min)</th>
                </tr>
              </thead>
              <tbody className='text-gray-700 fw-semibold'>
                {machines.length === 0 && (
                  <tr>
                    <td colSpan={3} className='text-center text-muted py-8'>
                      Nenhuma máquina cadastrada
                    </td>
                  </tr>
                )}
                {machines.map((machine) => (
                  <tr key={machine.id}>
                    <td>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={machine.linked}
                          onChange={() => update(machine.id, {linked: !machine.linked})}
                        />
                      </div>
                    </td>
                    <td
                      role='button'
                      className={machine.linked ? 'text-gray-900 fw-bold' : 'text-muted'}
                      onClick={() => update(machine.id, {linked: !machine.linked})}
                    >
                      {machine.name}
                      {machine.description && (
                        <span className='d-block text-muted fs-7 fw-normal'>{machine.description}</span>
                      )}
                    </td>
                    <td>
                      <input
                        type='number'
                        min={1}
                        max={32767}
                        step={1}
                        className={`form-control form-control-sm form-control-solid w-125px ${
                          machine.linked && !(machine.sessionTime >= 1) ? 'is-invalid' : ''
                        }`}
                        disabled={!machine.linked}
                        value={machine.sessionTime || ''}
                        onChange={(e) =>
                          update(machine.id, {sessionTime: Math.trunc(Number(e.target.value)) || 0})
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='card-footer d-flex justify-content-end align-items-center gap-4 py-6 px-9'>
        {invalid.length > 0 && (
          <span className='text-danger fs-7'>Informe o tempo de sessão das máquinas marcadas.</span>
        )}
        <button
          type='button'
          className='btn btn-danger'
          disabled={loading || saving || invalid.length > 0}
          onClick={save}
        >
          {!saving && 'Salvar máquinas'}
          {saving && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Salvando... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
